import { z } from 'zod';

import type { CustomerConfig } from './customer-config';

export enum AlertType {
  SAFETY = 'safety',
  IMBALANCE = 'imbalance',
  SOC_OFFSET = 'soc_offset',
  PERFORMANCE = 'performance',
  INACTIVITY = 'inactivity',
  PCS_EFFICIENCY = 'pcs_efficiency',
  PCS_POWER_AVAILABILITY = 'power_availability',
  PCS_POWER_CAPABILITY = 'power_capability',
  WARRANTY_CONDITION = 'warranty_condition',
  WARRANTY_PENALTY = 'warranty_penalty',
  WARRANTY_TAG = 'warranty_tag',
  WARRANTY_TAG_PCS = 'warranty_tag_pcs',
  WARRANTY_RTE = 'warranty_rte',
  WARRANTY_SOH = 'warranty_soh',
  NO_FINDING = 'no_finding',
}

export const AlertTypeSchema = z.nativeEnum(AlertType);

export const alertTypeMapping: Record<AlertType, string> = {
  safety: 'Safety',
  imbalance: 'SOC Imbalance',
  soc_offset: 'SOC Offset',
  performance: 'Performance',
  inactivity: 'Inactivity',
  pcs_efficiency: 'PCS Efficiency',
  power_availability: 'PCS Availability',
  power_capability: 'PCS Capability',
  warranty_condition: 'Warranty Condition',
  warranty_penalty: 'Warranty Penalty',
  warranty_tag: 'Warranty TAG',
  warranty_tag_pcs: 'Warranty TAG PCS',
  warranty_rte: 'Warranty RTE',
  warranty_soh: 'Warranty SOH',
  no_finding: 'No Finding',
};

// Map AlertType enum values to API string values (for API calls)
export const alertTypeToApi: Record<AlertType, string> = {
  [AlertType.SAFETY]: 'safety',
  [AlertType.IMBALANCE]: 'imbalance',
  [AlertType.SOC_OFFSET]: 'soc_offset',
  [AlertType.PERFORMANCE]: 'performance',
  [AlertType.INACTIVITY]: 'inactivity',
  [AlertType.PCS_POWER_AVAILABILITY]: 'power_availability',
  [AlertType.PCS_EFFICIENCY]: 'pcs_efficiency',
  [AlertType.PCS_POWER_CAPABILITY]: 'power_capability',
  [AlertType.WARRANTY_CONDITION]: 'warranty_condition',
  [AlertType.WARRANTY_PENALTY]: 'warranty_penalty',
  [AlertType.WARRANTY_TAG]: 'warranty_tag',
  [AlertType.WARRANTY_TAG_PCS]: 'warranty_tag_pcs',
  [AlertType.WARRANTY_RTE]: 'warranty_rte',
  [AlertType.WARRANTY_SOH]: 'warranty_soh',
  [AlertType.NO_FINDING]: 'no_finding',
};

// Helper function to convert AlertType[] to API string array (filtering unsupported types)
export function alertTypesToApi(
  types?: AlertType[],
):
  | (
      | 'soc_offset'
      | 'no_finding'
      | 'performance'
      | 'imbalance'
      | 'safety'
      | 'power_availability'
      | 'pcs_efficiency'
      | 'power_capability'
      | 'warranty_condition'
      | 'warranty_penalty'
      | 'warranty_tag'
      | 'warranty_tag_pcs'
      | 'warranty_rte'
      | 'warranty_soh'
    )[]
  | undefined {
  if (!types) return undefined;

  return types.map(
    (type) =>
      alertTypeToApi[type] as
        | 'soc_offset'
        | 'no_finding'
        | 'performance'
        | 'imbalance'
        | 'safety'
        | 'power_availability'
        | 'pcs_efficiency'
        | 'power_capability'
        | 'warranty_condition'
        | 'warranty_penalty'
        | 'warranty_tag'
        | 'warranty_tag_pcs'
        | 'warranty_rte'
        | 'warranty_soh',
  );
}

/**
 * Map alert types to their corresponding feature flags
 * This is used to determine if an alert type requires a specific feature flag
 * to be enabled for the customer
 */
export const alertTypeToFeatureFlag: Partial<
  Record<AlertType, keyof CustomerConfig['featureFlags']>
> = {
  // PCS-related alerts
  [AlertType.PCS_EFFICIENCY]: 'pcs',
  [AlertType.PCS_POWER_AVAILABILITY]: 'pcs',
  [AlertType.PCS_POWER_CAPABILITY]: 'pcs',

  // Warranty-related alerts
  [AlertType.WARRANTY_CONDITION]: 'warrantyAlerts',
  [AlertType.WARRANTY_PENALTY]: 'warrantyAlerts',
  [AlertType.WARRANTY_TAG]: 'warrantyAlerts',
  [AlertType.WARRANTY_TAG_PCS]: 'warrantyAlerts',
  [AlertType.WARRANTY_RTE]: 'warrantyAlerts',
  [AlertType.WARRANTY_SOH]: 'warrantyAlerts',
};
